// RejectionDialog — confirm a "reject" decision with an optional reason.
//
// Used wherever a reviewer rejects an agent-produced artefact (review
// queue rows, narrative drafts, task-level reject). The reason is
// optional — server actions already fall back to a generic
// "Rejected by user" string when omitted — but capturing it cheaply
// here lets the IM tell ATLAS *why* without a forced typing step.

import { useEffect, useState } from 'react';
import { ThumbsDown, RotateCcw } from 'lucide-react';
import { Modal, Button } from '@/components/ui';
import { cn } from '@/lib/utils';

interface RejectionDialogProps {
    open: boolean;
    /** Short title — e.g. "Reject this draft?". */
    title?: string;
    /** Optional secondary line: what's being rejected. */
    subject?: string;
    /** Placeholder text inside the textarea. */
    placeholder?: string;
    /** Confirm-button label. Defaults to "Reject". */
    confirmLabel?: string;
    /** Visual tone. `danger` (default) is the red reject styling; `neutral`
     *  re-skins the same dialog in the accent colour for non-destructive
     *  reasons-with-text decisions such as "Request revision". */
    tone?: 'danger' | 'neutral';
    /** Fires with the reason (or empty string) when the user confirms. */
    onConfirm: (reason: string) => void;
    onClose: () => void;
}

export default function RejectionDialog({
    open,
    title = 'Reject draft?',
    subject,
    placeholder = 'Optional — note why so the agent can re-roll without the same mistake.',
    confirmLabel = 'Reject',
    tone = 'danger',
    onConfirm,
    onClose,
}: RejectionDialogProps) {
    const [reason, setReason] = useState('');
    const Icon = tone === 'neutral' ? RotateCcw : ThumbsDown;

    // Reset the textarea when the dialog is reopened.
    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    if (!open) return null;

    const submit = () => {
        onConfirm(reason.trim());
        onClose();
    };

    return (
        <Modal
            isOpen={open}
            onClose={onClose}
            title={title}
            size="sm"
            footer={
                <>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant={tone === 'neutral' ? 'primary' : 'danger'}
                        size="sm"
                        icon={<Icon className="h-3.5 w-3.5" />}
                        onClick={submit}
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3">
                {subject && (
                    <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                        {subject}
                    </p>
                )}
                <label className="flex flex-col gap-1.5">
                    <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                        Reason (optional)
                    </span>
                    <textarea
                        value={reason}
                        autoFocus
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={placeholder}
                        rows={4}
                        onKeyDown={(e) => {
                            // Cmd/Ctrl+Enter submits — saves the IM from grabbing the mouse.
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                e.preventDefault();
                                submit();
                            }
                        }}
                        className={cn(
                            'w-full px-3 py-2 rounded-card text-body-medium resize-y',
                            'bg-[rgb(var(--color-surface-secondary))]',
                            'border border-[rgb(var(--color-divider))]',
                            'text-[rgb(var(--color-text-primary))]',
                            'placeholder:text-[rgb(var(--color-text-tertiary))]',
                            'focus:outline-none focus:border-[rgb(var(--color-accent))]',
                            'transition-colors duration-card-hover ease-swiss',
                        )}
                    />
                    <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                        ⌘+Enter to submit
                    </span>
                </label>
            </div>
        </Modal>
    );
}
